import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { StyledSummaryContainer, StyledSummaryHeader, StyledSummaryHeaderLeft, StyledAISummaryTag, StyledCollapseToggle, StyledCollapsibleContent, StyledSummaryToggleLink } from "./styled";
import { MarkdownStyled, MarkdownRenderer } from "@/components/AiChat/comps/Messages/AI";
import { ButtonTertiaryCompact } from "oolib/dist/v2/components/Buttons";
import axios from "axios";
import { useMutation } from "react-query";
import { icons, SkeletonLoader, UI_BODY_SM, UI_CAPTION, colors2 } from "oolib";
import { useGenErrorMsgBanners } from "@/utils/useGenErrorMsgBanners";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useGetAnnotationsSummary } from "@/utils/react-query-hooks/annotationSummary";

const useGenerateSummary = () => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();
  return useMutation(
    ({
      activeFilters,
      dataSource,
      customPrompt,
      findQuery,
      concurrency,
      processMode,
    }) =>
      axios.post("/api/annotationExplorer/generateSummary", {
        activeFilters,
        dataSource,
        customPrompt,
        findQuery,
        concurrency,
        processMode,
      }),
    {
      onSuccess: () => {},
      onError: (err) => {
        genErrorMsgBanners({ err });
      },
    }
  );
};


const useSummary = ({
  activeFilters,
  dataSource,
  findQuery = {},
  totalCount,
  searchTerm,
}) => {
  const {
    ai: { aiSummarization },
  } = useGetQueryData("platformConfigs");

  let limit = 0;

  try {
    limit = parseInt(aiSummarization?.defaultConfig?.limit || 0);
  } catch (error) {
    console.log(error);
  }

  const [summary, setSummary] = useState("");
  const { mutate: generateSummary, status: generateSummaryStatus } =
    useGenerateSummary();

  const handleOnClickGenerateSummary = () => {
    if (buttonDisabled) return;
    generateSummary(
      {
        activeFilters,
        dataSource,
        findQuery,
      },
      { onSuccess: ({ data }) => setSummary(data.summary) }
    );
  };

  const limitExceeded = totalCount > limit;

  const buttonDisabled =
    generateSummaryStatus === "loading" ||
    limitExceeded ||
    !aiSummarization?.enable ||
    searchTerm.length;

  const buttonText =
    generateSummaryStatus === "loading"
      ? "Generating..."
      : summary?.length > 0
      ? "Summarize Again"
      : "Summarize";

  const tooltipText = !aiSummarization?.enable
    ? "AI summarization is not enabled"
    : searchTerm?.length
    ? "Summarization is only available when no search term is applied. Please clear your search to enable summarization."
    : limitExceeded
    ? `To ensure optimal performance, only ${limit} ${dataSource} can be summarized at once. Please apply filters to narrow down your selection.`
    : "";

  return {
    summary,
    generateSummaryStatus,
    handleOnClickGenerateSummary,
    buttonDisabled,
    buttonText,
    tooltipText,
  };
};


const Summary = ({
  summary,
  onRefresh,
  isRefreshing,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const contentRef = useRef(null);

  return (
    <StyledSummaryContainer>
      <StyledSummaryHeader>
        <StyledSummaryHeaderLeft>
          <StyledAISummaryTag>AI SUMMARY</StyledAISummaryTag>
          <ButtonTertiaryCompact
            icon="ArrowsClockwise"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? "Refreshing..." : "Refresh Summary"}
          </ButtonTertiaryCompact>
        </StyledSummaryHeaderLeft>
        <StyledCollapseToggle onClick={() => setIsExpanded(prev => !prev)}>
          {isExpanded
            ? <icons.CaretUp size={16} weight="bold" />
            : <icons.CaretDown size={16} weight="bold" />
          }
        </StyledCollapseToggle>
      </StyledSummaryHeader>
      <StyledCollapsibleContent $height={isExpanded ? contentRef.current?.scrollHeight : 0}>
        <div ref={contentRef}>
          <MarkdownStyled>{summary}</MarkdownStyled>
        </div>
      </StyledCollapsibleContent>
    </StyledSummaryContainer>
  );
};

// ===================================================================
// V2 — ReAct agent-based annotation summary (auto-fetch, cached)
// ===================================================================

/**
 * Fetches the V2 summary for the current scope. Auto-runs on mount and
 * whenever contentTypes or activeFilters change. Backend handles caching —
 * a hit is cheap, a miss runs the ReAct pipeline.
 */
const useAnnoSummaryV2 = ({ activeFilters, contentTypes, enabled = true }) => {
  const { genErrorMsgBanners } = useGenErrorMsgBanners();

  // Consistent with AI Chat / WidgetAskAI: contentTypes are sent as a
  // separate field (not as a filter inside activeFilters). Strip the
  // synthetic `contentTypeFilter_contentType` that the AnnoExplorer
  // page prepends for its own data fetching.
  const filtersWithoutCT = (activeFilters || []).filter(
    (f) => f?.filterId !== "contentTypeFilter_contentType"
  );

  // Lazy generation: an LLM run only happens after the user explicitly triggers
  // it via `generate()`. Before that the same request goes out as a cacheOnly
  // probe, so a scope the BE has already summarized (it keeps them for 24h)
  // renders its summary on mount instead of the CTA — no LLM call either way.
  // We auto-reset the trigger whenever the scope
  // (contentTypes + activeFilters) changes — a new scope means the prior
  // summary is no longer relevant and the user should opt in again rather
  // than us silently kicking off a fresh expensive run.
  const [hasTriggered, setHasTriggered] = useState(false);
  const scopeKey = useMemo(
    () => JSON.stringify({ contentTypes, filtersWithoutCT }),
    [contentTypes, filtersWithoutCT]
  );
  const prevScopeKeyRef = useRef(scopeKey);
  useEffect(() => {
    if (prevScopeKeyRef.current !== scopeKey) {
      prevScopeKeyRef.current = scopeKey;
      setHasTriggered(false);
    }
  }, [scopeKey]);

  // THE shared annotation-summary hook (POST /api/annotationSummary).
  //
  // Sends "dynamic" rather than a pinned strategy name: the BE then routes small
  // scopes to singleShot and larger ones to CONFIG.largeScopeStrategy, which is
  // the single place the summary strategy is chosen. Naming a strategy here
  // instead would bypass that router and silently ignore the switch (this line
  // was pinned to "aiChat" until 2026-07-31). Behaviour is unchanged today —
  // largeScopeStrategy still defaults to "aiChat".
  const { data, status, error, refetch, isFetching } = useGetAnnotationsSummary({
    scope: { activeFilters: filtersWithoutCT, contentTypes: contentTypes || null },
    strategy: "dynamic",
    cacheOnly: !hasTriggered,
    enabled,
    queryOptions: { onError: (err) => genErrorMsgBanners({ err }) },
  });

  return {
    summary: data?.summary || "",
    // Condensed companion — present only when the BE routes to
    // countFirst-saturate; null elsewhere (and on pre-change cache entries).
    shortSummary: data?.shortSummary || null,
    empty: data?.empty === true,
    cached: data?.cached,
    annotationCount: data?.annotationCount,
    // A probe answer stands in for a trigger: there is something to show —
    // a summary, or the empty-scope state — so the CTA would be wrong.
    hasTriggered: hasTriggered || !!data?.summary || data?.empty === true,
    // Covers the probe too, otherwise the CTA renders while it is in flight and
    // is then replaced by the summary: the very flash this change removes.
    loading: status === "loading" || (isFetching && !data),
    isRefreshing: isFetching && !!data,
    error,
    generate: () => setHasTriggered(true),
    refresh: refetch,
  };
};

/**
 * Renders the summary card.
 *
 * Behaviour is lazy: nothing fetches until the user clicks "Summarize". A
 * fresh scope (different filters or contentTypes) resets the trigger so the
 * user explicitly opts in for each filter set, rather than us silently
 * burning LLM calls every time they tweak a filter.
 */
const SummaryV2 = ({ summary, shortSummary, empty, hasTriggered, loading, isRefreshing, onGenerate, onRefresh, style = {} }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  // Short-by-default when a condensed companion exists; without one, the long
  // summary renders exactly as before and no toggle appears.
  const [showDetailed, setShowDetailed] = useState(false);
  const contentRef = useRef(null);

  // Measured AFTER commit: reading scrollHeight at render time would clamp the
  // collapsible to the PREVIOUS content's height when toggling short↔detailed.
  const [contentHeight, setContentHeight] = useState(0);
  useLayoutEffect(() => {
    if (contentRef.current) setContentHeight(contentRef.current.scrollHeight);
  }, [showDetailed, summary, shortSummary, isExpanded]);

  // Pre-generation idle state — show the call-to-action button. This is the
  // default until the user clicks Summarize for the current filter scope.
  if (!hasTriggered) {
    return (
      <StyledSummaryContainer style={style}>
        <StyledSummaryHeader>
          <StyledSummaryHeaderLeft>
            <StyledAISummaryTag>AI SUMMARY</StyledAISummaryTag>
            <ButtonTertiaryCompact icon="Sparkle" onClick={onGenerate}>
              Summarize
            </ButtonTertiaryCompact>
          </StyledSummaryHeaderLeft>
        </StyledSummaryHeader>
        <div style={{ padding: "1rem 1.6rem" }}>
          <UI_BODY_SM style={{ color: colors2.grey50 }}>
            Click Summarize to generate an AI summary of the annotations matching your current filters.
          </UI_BODY_SM>
        </div>
      </StyledSummaryContainer>
    );
  }

  // While loading after a trigger, show a placeholder card so the layout
  // doesn't pop in when the summary arrives.
  if (loading) {
    return (
      <StyledSummaryContainer style={style}>
        <StyledSummaryHeader>
          <StyledSummaryHeaderLeft>
            <StyledAISummaryTag>AI SUMMARY</StyledAISummaryTag>
            <UI_BODY_SM style={{ color: colors2.grey50 }}>Generating...</UI_BODY_SM>
          </StyledSummaryHeaderLeft>
        </StyledSummaryHeader>
        <div style={{ padding: "1rem 1.6rem" }}>
          <SkeletonLoader style={{ height: "80px" }} />
        </div>
      </StyledSummaryContainer>
    );
  }

  // Backend short-circuits with `empty: true` when the current scope has zero
  // annotations — render an explicit empty-state instead of nothing, so users
  // understand the summary isn't missing, it's just N/A for this filter set.
  if (empty) {
    return (
      <StyledSummaryContainer style={style}>
        <StyledSummaryHeader>
          <StyledSummaryHeaderLeft>
            <StyledAISummaryTag>AI SUMMARY</StyledAISummaryTag>
          </StyledSummaryHeaderLeft>
        </StyledSummaryHeader>
        <div style={{ padding: "1rem 1.6rem" }}>
          <UI_BODY_SM style={{ color: colors2.grey50 }}>
            No annotations in the current scope — nothing to summarize.
          </UI_BODY_SM>
        </div>
      </StyledSummaryContainer>
    );
  }

  if (!summary) return null;

  return (
    <StyledSummaryContainer style={style}>
      <StyledSummaryHeader>
        <StyledSummaryHeaderLeft>
          <StyledAISummaryTag>AI SUMMARY</StyledAISummaryTag>
          <ButtonTertiaryCompact
            icon="ArrowsClockwise"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? "Refreshing..." : "Refresh Summary"}
          </ButtonTertiaryCompact>
        </StyledSummaryHeaderLeft>
        <StyledCollapseToggle onClick={() => setIsExpanded(prev => !prev)}>
          {isExpanded
            ? <icons.CaretUp size={16} weight="bold" />
            : <icons.CaretDown size={16} weight="bold" />
          }
        </StyledCollapseToggle>
      </StyledSummaryHeader>
      <StyledCollapsibleContent $height={isExpanded ? contentHeight : 0}>
        <div ref={contentRef}>
          <MarkdownRenderer>{shortSummary && !showDetailed ? shortSummary : summary}</MarkdownRenderer>
          {shortSummary && (
            <StyledSummaryToggleLink type="button" onClick={() => setShowDetailed((v) => !v)}>
              <UI_CAPTION>{showDetailed ? "View short summary" : "View detailed summary"}</UI_CAPTION>
            </StyledSummaryToggleLink>
          )}
        </div>
      </StyledCollapsibleContent>
    </StyledSummaryContainer>
  );
};

export { useGenerateSummary, useSummary, Summary, useAnnoSummaryV2, SummaryV2 };
export { DocumentSummary, useGetDocumentSummary } from "./DocumentSummary";
